import { useState, useEffect, useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import {
  LayoutDashboard,
  LayoutGrid,
  DollarSign,
  Package,
  ClipboardCheck,
  Users,
  Wallet,
  Clock,
  FileText,
  Bell,
  Activity,
  Server,
  Megaphone,
  Calendar,
  Target,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Building2,
  PackageSearch,
  TrendingUp,
  AlertTriangle,
  Receipt,
  Scale,
  Radio,
  Share2,
  ShoppingCart,
  Search,
  Microscope,
  Calculator,
  Sparkles,
  Smartphone,
  Link2,
  CopyCheck,
  Eye,
  Coins,
  Gift,
  Boxes,
  Layers,
  Tag,
  MapPin,
  FlaskConical,
  MessageSquare,
  PackagePlus,
  TrendingDown,
  Flame,
  ScanLine,
  Landmark,
  ListChecks,
  Store,
  Cog,
  X,
  GitCompare,
  PackageCheck,
  ScanSearch,
  CreditCard,
  type LucideIcon,
} from 'lucide-react'

type NavItem = { name: string; href: string; icon: LucideIcon }
type NavGroup = { name: string; icon: LucideIcon; items: NavItem[] }

// Grouped navigation. Order = how often the owner reaches for each area.
const navGroups: NavGroup[] = [
  {
    name: 'Overview',
    icon: LayoutDashboard,
    items: [
      { name: 'Dashboard', href: '/', icon: LayoutDashboard },
      { name: 'Sales & Income', href: '/sales', icon: DollarSign },
      { name: 'Payment Requests', href: '/payment-requests', icon: CreditCard },
      { name: 'Profit & Loss', href: '/profit', icon: TrendingUp },
      { name: 'Reports', href: '/reports', icon: FileText },
      { name: 'Goals & KPIs', href: '/goals', icon: Target },
    ],
  },
  {
    name: 'Inventory',
    icon: Package,
    items: [
      { name: 'Inventory', href: '/inventory', icon: Package },
      { name: 'Inventory Tasks', href: '/inventory-tasks', icon: ClipboardCheck },
      { name: 'Stock Alerts', href: '/stock-alerts', icon: AlertTriangle },
      { name: 'Dispense Soon', href: '/dispense-soon', icon: Clock },
      { name: 'Drug Intelligence', href: '/drug-intelligence', icon: Microscope },
      { name: 'Prescription Calculator', href: '/prescription-calculator', icon: Calculator },
      { name: 'Medical Reps', href: '/medical-reps', icon: Users },
    ],
  },
  {
    name: 'Purchasing',
    icon: ShoppingCart,
    items: [
      { name: 'Suppliers', href: '/suppliers', icon: Building2 },
      { name: 'Supplier Balance Check', href: '/supplier-balance-check', icon: Scale },
      { name: 'Mercato Search', href: '/mercato', icon: Search },
      { name: 'Mercato Catalog', href: '/mercato-browse', icon: Package },
      { name: 'Mercato Suppliers', href: '/mercato-suppliers', icon: Building2 },
      { name: 'Capsula Suppliers', href: '/capsula-suppliers', icon: PackageCheck },
      { name: 'Capsula Catalog', href: '/capsula-catalog', icon: ScanSearch },
      { name: 'Capsula Disagreements', href: '/capsula-disagreements', icon: GitCompare },
      { name: 'Purchase Requests', href: '/purchase-requests', icon: ShoppingCart },
      { name: 'Most Requested', href: '/purchase-demand', icon: Flame },
      { name: 'Expenses', href: '/expenses', icon: Receipt },
    ],
  },
  {
    name: 'Products & Catalog',
    icon: Tag,
    items: [
      { name: 'Product Review', href: '/enrichment', icon: PackageSearch },
      { name: 'Supplement Review', href: '/supplement-review-queue', icon: FlaskConical },
      { name: 'Category Merge', href: '/category-merge', icon: Layers },
      { name: 'Brands', href: '/brands', icon: Tag },
      { name: 'BSO Link', href: '/bso-link', icon: Smartphone },
      { name: 'VSH Link', href: '/vsh-link', icon: Boxes },
      { name: 'PharmaZone Link', href: '/pharmazone-link', icon: Boxes },
      { name: 'POS Link Suggestions', href: '/pos-link-suggestions', icon: Link2 },
      { name: 'Duplicate review', href: '/duplicate-review', icon: CopyCheck },
      { name: 'Order Matches', href: '/order-item-matches', icon: Link2 },
    ],
  },
  {
    name: 'Pricing',
    icon: Coins,
    items: [
      { name: 'Pricing Engine', href: '/pricing-settings', icon: Coins },
      { name: 'Margin Review', href: '/margin-review', icon: TrendingDown },
      { name: 'Price Reviews', href: '/price-reviews', icon: ScanLine },
      { name: 'MoH Prices', href: '/moh-prices', icon: Landmark },
      { name: 'MoH Match Review', href: '/moh-match', icon: ListChecks },
    ],
  },
  {
    name: 'Online Store & Marketing',
    icon: Store,
    items: [
      { name: 'Ads Manager', href: '/ads', icon: Radio },
      { name: 'Social Media', href: '/social', icon: Share2 },
      { name: 'Banner Studio', href: '/banner-studio', icon: Sparkles },
      { name: 'Homepage Editor', href: '/homepage-editor', icon: LayoutGrid },
      { name: 'Search Analytics', href: '/search-analytics', icon: PackageSearch },
      { name: 'Customer Feedback', href: '/customer-feedback', icon: MessageSquare },
      { name: 'Loyalty Points', href: '/loyalty', icon: Gift },
      { name: 'Product Requests', href: '/product-requests', icon: PackagePlus },
      { name: 'App Visibility', href: '/app-visibility', icon: Eye },
      { name: 'Delivery Accounts', href: '/delivery-reconciliation', icon: Coins },
    ],
  },
  {
    name: 'Team',
    icon: Users,
    items: [
      { name: 'Staff', href: '/staff', icon: Users },
      { name: 'Salary', href: '/salary', icon: Wallet },
      { name: 'Attendance', href: '/attendance', icon: Clock },
      { name: 'Branches', href: '/branches', icon: MapPin },
    ],
  },
  {
    name: 'System & Comms',
    icon: Cog,
    items: [
      { name: 'Notifications', href: '/notifications', icon: Bell },
      { name: 'Notification Settings', href: '/notification-settings', icon: Bell },
      { name: 'Announcements', href: '/announcements', icon: Megaphone },
      { name: 'Calendar', href: '/calendar', icon: Calendar },
      { name: 'App Config', href: '/app-config', icon: Settings },
      { name: 'Activity Log', href: '/activity', icon: Activity },
      { name: 'System Health', href: '/system', icon: Server },
      { name: 'Settings', href: '/settings', icon: Settings },
    ],
  },
]

const COLLAPSED_KEY = 'ghp-sidebar-collapsed'
const EXPANDED_KEY = 'ghp-sidebar-expanded-groups'

function loadExpanded(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(EXPANDED_KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return {}
}

type SidebarProps = { mobileOpen: boolean; onClose: () => void }

export default function Sidebar({ mobileOpen, onClose }: SidebarProps) {
  const location = useLocation()
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === '1')
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState<Record<string, boolean>>(loadExpanded)

  // Rail (icon-only) mode is a DESKTOP-only affordance. On mobile the drawer
  // always renders the full expanded menu regardless of the saved collapsed state.
  const rail = collapsed && isDesktop

  // Close the mobile drawer whenever the route changes (a no-op on desktop
  // where mobileOpen is always false).
  useEffect(() => {
    onClose()
  }, [location.pathname, onClose])

  // Which group owns the current route.
  const activeGroup = useMemo(
    () => navGroups.find((g) => g.items.some((i) => i.href === location.pathname))?.name,
    [location.pathname]
  )

  // Always keep the active route's group open.
  useEffect(() => {
    if (activeGroup) {
      setExpanded((prev) => (prev[activeGroup] ? prev : { ...prev, [activeGroup]: true }))
    }
  }, [activeGroup])

  useEffect(() => {
    try { localStorage.setItem(EXPANDED_KEY, JSON.stringify(expanded)) } catch { /* ignore */ }
  }, [expanded])

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0')
  }, [collapsed])

  const toggleGroup = (name: string) =>
    setExpanded((prev) => ({ ...prev, [name]: !prev[name] }))

  // Flat search results across every group.
  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return null
    return navGroups
      .flatMap((g) => g.items.map((i) => ({ ...i, group: g.name })))
      .filter((i) => i.name.toLowerCase().includes(q) || i.group.toLowerCase().includes(q))
  }, [query])

  const itemLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn('sidebar-link', isActive && 'active', rail && 'justify-center px-2')

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={cn(
          'flex flex-col bg-white border-r border-slate-200',
          // Mobile: off-canvas drawer that slides in over the content.
          'fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
          // Desktop (lg+): part of the normal flex flow, width follows rail state.
          // Below lg (phones in portrait AND landscape) it stays an off-canvas drawer.
          'lg:static lg:translate-x-0 lg:transition-all',
          rail ? 'lg:w-20' : 'lg:w-64'
        )}
      >
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200">
        {!rail ? (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center p-1.5">
              <img src="/assets/GHP Logo White heart.svg" alt="GH Pharmacy" className="w-full h-full object-contain" />
            </div>
            <span className="font-semibold text-lg text-slate-900">Pharmacy</span>
          </div>
        ) : (
          <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center mx-auto p-1.5">
            <img src="/assets/GHP Logo White heart.svg" alt="GH Pharmacy" className="w-full h-full object-contain" />
          </div>
        )}
        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="lg:hidden p-1.5 rounded-lg text-slate-500 hover:bg-slate-100"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Search (hidden when rail-collapsed) */}
      {!rail && (
        <div className="px-3 pt-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search pages…"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-8 pr-7 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 custom-scrollbar">
        {/* Search results — flat list across all groups */}
        {searchResults ? (
          <ul className="space-y-1 px-3">
            {searchResults.length === 0 && (
              <li className="px-3 py-2 text-sm text-slate-400">No pages match “{query}”.</li>
            )}
            {searchResults.map((item) => (
              <li key={item.href}>
                <NavLink to={item.href} className={itemLinkClass} title={item.name}>
                  <item.icon className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1">{item.name}</span>
                  <span className="text-[10px] text-slate-400">{item.group}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        ) : rail ? (
          /* Rail mode — group icons with hover flyout */
          <ul className="space-y-1 px-3">
            {navGroups.map((group) => (
              <li key={group.name} className="relative group">
                <button
                  onClick={() => { setCollapsed(false); setExpanded((p) => ({ ...p, [group.name]: true })) }}
                  className={cn(
                    'sidebar-link justify-center px-2 w-full',
                    activeGroup === group.name && 'text-primary'
                  )}
                  title={group.name}
                >
                  <group.icon className="w-5 h-5 flex-shrink-0" />
                </button>
                {/* Flyout */}
                <div className="absolute left-full top-0 ml-2 hidden group-hover:block z-50 w-56 rounded-lg border border-slate-200 bg-white py-2 shadow-lg">
                  <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">{group.name}</div>
                  {group.items.map((item) => (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      className={({ isActive }) => cn('sidebar-link mx-1', isActive && 'active')}
                    >
                      <item.icon className="w-4 h-4 flex-shrink-0" />
                      <span>{item.name}</span>
                    </NavLink>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          /* Expanded — accordion groups */
          <div className="space-y-0.5 px-3">
            {navGroups.map((group) => {
              const isOpen = !!expanded[group.name]
              return (
                <div key={group.name}>
                  <button
                    onClick={() => toggleGroup(group.name)}
                    aria-expanded={isOpen}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold transition-colors',
                      'text-slate-500 hover:bg-slate-100',
                      activeGroup === group.name && !isOpen && 'text-primary'
                    )}
                  >
                    <group.icon className="w-5 h-5 flex-shrink-0" />
                    <span className="flex-1 text-left">{group.name}</span>
                    <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen ? 'rotate-180' : '')} />
                  </button>
                  <div
                    className={cn(
                      'overflow-hidden transition-all duration-200',
                      isOpen ? 'max-h-[40rem]' : 'max-h-0'
                    )}
                  >
                    <ul className="mt-0.5 mb-1 space-y-0.5 pl-3 border-l border-slate-100 ml-4">
                      {group.items.map((item) => (
                        <li key={item.href}>
                          <NavLink to={item.href} className={({ isActive }) => cn('sidebar-link', isActive && 'active')}>
                            <item.icon className="w-4 h-4 flex-shrink-0" />
                            <span>{item.name}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </nav>

      {/* Collapse toggle — desktop only (the mobile drawer uses the X / backdrop) */}
      <div className="hidden lg:block border-t border-slate-200 p-3">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span>Collapse</span>
            </>
          )}
        </button>
      </div>
      </aside>
    </>
  )
}
